/**
 * Single-device fertigation rule helpers.
 *
 * Business rule (UI-enforced; no DB constraint yet):
 * All fertigation for a project must be automated by a single PumpLink device.
 *
 * Two cases:
 *  - Case A — the project has a dedicated fertigation pump
 *    (`project.fertigation_water_pump` is set). Every tank VDC must use the
 *    SAME device the fertigation pump is wired to. The pump's own output index
 *    must be excluded from the tank output picker.
 *  - Case B — no fertigation pump (per-tank pumps). All tanks share one device;
 *    the first tank assignment fixes it, later tanks are locked to it.
 *
 * These helpers are pure (no React/jotai coupling) so they can be unit-tested
 * directly. Callers pass the relevant maps/atoms.
 */
import type { AUTProject } from "@/api/queries/account";
import type { VirtualDeviceConfigurationWithModel } from "@/store/data";

/**
 * Shape of a project as needed by the resolver. Kept narrow so the helper can
 * be called with partial/test data.
 */
export type FertigationProjectInput = Pick<AUTProject, "fertigation_water_pump" | "project_tanks"> | null | undefined;

export interface ResolvedFertigationDevice {
  /**
   * Which business case applied.
   * - "A": project has a dedicated fertigation pump.
   * - "B": no fertigation pump (per-tank pumps).
   */
  case: "A" | "B";
  /**
   * The single PumpLink device id all tank VDCs for the project must use.
   * - Case A: the fertigation pump's device (resolved from its pump-VDC).
   * - Case B: the device shared by the project's existing active tank VDCs;
   *   `null` when no active tank VDC exists yet (unrestricted / first-tank-wins).
   */
  deviceId: string | null;
  /**
   * Case A only: the fertigation pump's own `turn_on_physical_output_index`,
   * which must be excluded from the tank output picker. `null` in Case B.
   */
  fertigationPumpOutputIndex: number | null;
  /**
   * Distinct PumpLink device ids currently used by the project's ACTIVE tank
   * VDCs. Callers use this to detect (and toast on) an inconsistent split
   * across devices — which should not happen under the rule but can exist
   * historically. Length 0 means no active tank VDC yet.
   */
  existingTankDeviceIds: string[];
  /**
   * Number of active (non-ended) `project_tank` assignments, regardless of
   * whether they have a VDC. Drives the single-tank edit exception: in Case B
   * with exactly one active project_tank, editing that tank may move it to any
   * PumpLink (there is no sibling to conflict with).
   */
  activeTankCount: number;
}

/**
 * The device ids of the active (non-ended) tank VDCs attached to a project,
 * de-duplicated and order-stable.
 *
 * Reads from `project_tanks[].vdc_configurations`, keeping only rows where
 * `equipment_type === "tank"` and `end_date` is null/absent.
 */
export function getActiveTankDeviceIds(project: FertigationProjectInput): string[] {
  if (!project) return [];

  const deviceIds = new Set<string>();
  for (const pt of project.project_tanks ?? []) {
    // Only consider active tank assignments for the "shared device" lock.
    if (pt.end_date) continue;
    for (const vdc of pt.vdc_configurations ?? []) {
      if (vdc.equipment_type !== "tank") continue;
      if (vdc.end_date) continue;
      if (vdc.device) deviceIds.add(vdc.device);
    }
  }
  return [...deviceIds];
}

/**
 * Count of active (non-ended) `project_tank` assignments for a project,
 * regardless of whether they have a VDC. Used to decide whether the shared
 * device may be changed (only the single-tank Case B is free).
 */
export function getActiveProjectTankCount(project: FertigationProjectInput): number {
  if (!project) return 0;
  return (project.project_tanks ?? []).filter((pt) => !pt.end_date).length;
}

/**
 * Resolve the single allowed fertigation PumpLink device for a project.
 *
 * @param project The project (fertigation_water_pump + project_tanks).
 * @param waterPumpVdcMap Map of water-pump id -> its active pump-VDC (from
 *   `waterPumpVirtualDeviceConfigMapAtom`), used to resolve Case A.
 */
export function resolveFertigationDevice(
  project: FertigationProjectInput,
  waterPumpVdcMap: Map<string, VirtualDeviceConfigurationWithModel | null>
): ResolvedFertigationDevice {
  const existingTankDeviceIds = getActiveTankDeviceIds(project);
  const activeTankCount = getActiveProjectTankCount(project);

  // Case A — dedicated fertigation pump: allowed device is the pump's device.
  const fertigationPumpId = project?.fertigation_water_pump || null;
  if (fertigationPumpId) {
    const pumpVdc = waterPumpVdcMap.get(fertigationPumpId) ?? null;
    return {
      case: "A",
      deviceId: pumpVdc?.device?.id ?? null,
      fertigationPumpOutputIndex: pumpVdc?.turn_on_physical_output_index ?? null,
      existingTankDeviceIds,
      activeTankCount,
    };
  }

  // Case B — no fertigation pump: tanks must share one device; the first
  // active tank VDC locks it. null means no tank assigned yet (unrestricted).
  return {
    case: "B",
    deviceId: existingTankDeviceIds[0] ?? null,
    fertigationPumpOutputIndex: null,
    existingTankDeviceIds,
    activeTankCount,
  };
}
